import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import { Box, Radio } from '@node-space/ui-components'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { CustomerEntityType } from 'types/customers'
import { ErrorDetails } from 'types/types'
import {
  ConfirmBusinessCustomerMethod,
  ConfirmCustomerData,
  ConfirmIndividualCustomerMethod,
} from '../helpers/types'
import { AddBusinessCustomer } from './AddBusinessCustomer'
import { AddIndividualCustomer } from './AddIndividualCustomer'

export interface AddCustomerFormProps {
  defaultValues: ConfirmCustomerData
  onConfirm: (customerData: ConfirmCustomerData) => void
  onCancel: () => void
  fieldErrors?: ErrorDetails
  setFieldErrors?: (errors: ErrorDetails) => void
}

export const AddCustomerForm = ({
  defaultValues,
  onConfirm,
  onCancel,
  fieldErrors,
  setFieldErrors,
}: AddCustomerFormProps) => {
  const { t } = useTranslation()
  const { enableAddEmbeddedIndividual } = useFeatureFlags()
  const { accountCapabilities } = useAccountsContext()
  const {
    isEmbeddedCustomerCompanyCapabilityEnabled,
    isEmbeddedCustomerIndividualCapabilityEnabled,
  } = accountCapabilities || {}

  const [customerType, setCustomerType] = useState<string>(
    defaultValues?.individual || !isEmbeddedCustomerCompanyCapabilityEnabled
      ? CustomerEntityType.INDIVIDUAL
      : CustomerEntityType.COMPANY
  )

  const onConfirmBusinessCustomer: ConfirmBusinessCustomerMethod = businessCustomerData => {
    onConfirm({ business: businessCustomerData })
  }

  const onConfirmIndividualCustomer: ConfirmIndividualCustomerMethod = individualCustomerData => {
    onConfirm({ individual: individualCustomerData })
  }

  const customerTypeOptions = useMemo(
    () => [
      {
        value: CustomerEntityType.COMPANY,
        label: t('customers.business'),
        disabled: !isEmbeddedCustomerCompanyCapabilityEnabled,
      },
      {
        value: CustomerEntityType.INDIVIDUAL,
        label: t('customers.individual'),
        disabled: !isEmbeddedCustomerIndividualCapabilityEnabled || !enableAddEmbeddedIndividual,
      },
    ],
    [
      enableAddEmbeddedIndividual,
      isEmbeddedCustomerCompanyCapabilityEnabled,
      isEmbeddedCustomerIndividualCapabilityEnabled,
    ]
  )

  return (
    <Box flexCol gap={16}>
      <Radio
        value={customerType}
        onChange={value => {
          setCustomerType(value)
          setFieldErrors(null)
        }}
        horizontal
        options={customerTypeOptions}
      />
      {customerType === CustomerEntityType.COMPANY && (
        <AddBusinessCustomer
          defaultValues={defaultValues?.business?.data}
          onConfirm={onConfirmBusinessCustomer}
          onCancel={onCancel}
          fieldErrors={fieldErrors}

        />
      )}
      {customerType === CustomerEntityType.INDIVIDUAL && (
        <AddIndividualCustomer
          defaultValues={defaultValues?.individual?.data}
          onConfirm={onConfirmIndividualCustomer}
          onCancel={onCancel}
          fieldErrors={fieldErrors}
        />
      )}
    </Box>
  )
}
